import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import { site, siteLocales, i18next, dialogs } from '@yola/ws-sdk';
import { constants as subscriptionManagerConstants } from '@yola/subscription-manager-js';
import { reactHookForm } from '@yola/ws-ui';
import segment from 'src/js/modules/analytics/segment';
import saveSite from 'src/js/modules/saving/helpers/save-site';
import useFeatureFlags from 'yola-editor/src/js/modules/feature-flags/hooks/use-feature-flags';
import websiteSettings from 'src/js/modules/website-settings';
import dialogTypes from 'src/js/modules/dialogs/constants/dialog-types';
import tabIdentifiers from 'src/js/modules/website-settings/constants/tab-identifiers';
import upsell from '../../upsell';
import ai from '../../ai';
import reloadSiteMeta from '../../ai-wizard/helpers/reload-site-meta';
import TranslatePageDialog from '../components/translate-page-dialog';
import fieldNames from '../constants/field-names';
import translateExistingPageOptionsValues from '../constants/translate-existing-page-options-values';
import sortAlphabetically from '../../common/helpers/sort-alphabetically';
import useCheckForUnsafe from '../../website-settings/hooks/use-check-for-unsafe';

const { useForm } = reactHookForm;

const { helpers } = websiteSettings;

const {
  trackers: { trackEvent },
  constants: { events },
} = segment;

const {
  constants: { errorTypes },
  helpers: { initiatePageLocalization },
} = ai;

const {
  hostingPackageFeatureNames: { AI_LOCALIZATION, PAGE_COUNT },
} = subscriptionManagerConstants;

const getCaptions = ({ pagesToOverride, isOnePageToOverride, languageItems }) => {
  const pageName = pagesToOverride[0]?.name;

  return {
    title: i18next.t('Translate pages'),
    fields: {
      pages: {
        label: i18next.t('Select your pages'),
        placeholder: i18next.t('Select your page(s)'),
        searchPlaceholder: i18next.t('Select...'),
        selectAllLabel: i18next.t('Select all pages'),
        noResultsText: i18next.t('No pages found'),
        getSelectionLabel: (count) => i18next.t('{count} page(s) selected', { count }),
      },
      languages: {
        label: i18next.t('Translate to'),
        placeholder:
          languageItems.length === 0
            ? i18next.t('No languages found')
            : i18next.t('Select at least 1 language'),
        searchPlaceholder: i18next.t('Select...'),
        noResultsText: i18next.t('No languages found'),
        getSelectionLabel: (count) => i18next.t('{count} language(s) selected', { count }),
        notificationDescription: i18next.t(
          'To translate your pages, add a new language first in  {path}',
          { path: '' }
        ),
        notificationPath: i18next.t('Website Settings → Multilingual'),
      },
      overrideExistingPages: {
        title: i18next.t('Override existing page?'),
        description: isOnePageToOverride
          ? i18next.t(
              '"{pageName}" page in selected languages already exists. Override it or create a copy?',
              {
                pageName,
              }
            )
          : i18next.t(
              '{pageCount} pages in selected languages already exists. Override them or create copies?',
              {
                pageCount: pagesToOverride.length,
              }
            ),
        radioButton1: isOnePageToOverride
          ? i18next.t('Override "{pageName}"', { pageName })
          : i18next.t('Override pages'),
        radioButton2: i18next.t('Make a copy'),
      },
      autoTranslate: {
        label: i18next.t('Translate with'),
      },
    },
    submit: i18next.t('Submit'),
    cancel: i18next.t('Cancel'),
  };
};

const setInitialValue = (values) => ({
  [fieldNames.TRANSLATE_PAGE_IDS]: values[fieldNames.TRANSLATE_PAGE_IDS],
  [fieldNames.TRANSLATE_LOCALES]: values[fieldNames.TRANSLATE_LOCALES] || [],
  [fieldNames.TRANSLATE_AUTO_TRANS]: values[fieldNames.TRANSLATE_AUTO_TRANS] || false,
  [fieldNames.EXISTING_PAGE_RESOLVING]:
    values[fieldNames.EXISTING_PAGE_RESOLVING] || translateExistingPageOptionsValues.REPLACE_PAGE,
});

const sortTreeItemsAlphabetically = (items) => {
  const sortedItems = [...items].sort((a, b) => sortAlphabetically(a.value, b.value));

  return sortedItems.map((item) => {
    if (!item.items || item.items.length === 0) {
      return item;
    }

    return {
      ...item,
      items: sortTreeItemsAlphabetically(item.items),
    };
  });
};

function TranslatePageDialogContainer({
  captions: propsCaptions = {},
  initialValues,
  bulkEditing = false,
  onDialogMainAction,
  onDialogCancel,
}) {
  const {
    control,
    handleSubmit: manageSubmit,
    watch,
    getValues,
  } = useForm({
    defaultValues: setInitialValue(initialValues),
  });
  const selectedPageIds = watch(fieldNames.TRANSLATE_PAGE_IDS);
  const selectedLanguages = watch(fieldNames.TRANSLATE_LOCALES);

  const dispatch = useDispatch();
  const [featureFlags] = useFeatureFlags(['ai_localization']);
  const { ai_localization: isAiLocalizationEnabled } = featureFlags;
  const checkForUnsafe = useCheckForUnsafe();

  const pages = useSelector(site.selectors.getPages);
  const folders = useSelector(site.selectors.getFolders);
  const currentLocale = useSelector(siteLocales.selectors.getCurrentLocale);
  const usedLocales = useSelector(siteLocales.selectors.getSiteLocales);
  const supportedLocales = useSelector(siteLocales.selectors.getSupportedLocales);

  const countries = useMemo(() => helpers.getCountries(), []);
  const languages = useMemo(() => helpers.getLanguages(), []);

  const idToPage = useMemo(
    () =>
      new Map(
        pages.filter(({ locale }) => locale === currentLocale).map((page) => [page.id, page])
      ),
    [currentLocale, pages]
  );

  const { pageItems, initialExpandedPageItemIds } = useMemo(() => {
    const idToFolder = new Map(
      folders.filter(({ locale }) => locale === currentLocale).map((folder) => [folder.id, folder])
    );

    const idToFolderItem = new Map(
      Array.from(idToFolder.keys()).map((folderId) => {
        const folder = idToFolder.get(folderId);
        return [
          folderId,
          {
            id: folderId,
            value: folder.name,
            label: folder.name,
            items: [],
          },
        ];
      })
    );

    const rootItems = [];

    idToFolder.forEach((_, folderId) => {
      const folderItem = idToFolderItem.get(folderId);
      const folder = idToFolder.get(folderId);
      const { parentFolderId } = folder;

      if (parentFolderId) {
        idToFolderItem.get(parentFolderId).items.push(folderItem);
        return;
      }

      rootItems.push(folderItem);
    });

    idToPage.forEach((page) => {
      const pageItem = { id: page.id, value: page.name, label: page.name };
      const { parentFolderId } = page;

      if (parentFolderId) {
        idToFolderItem.get(parentFolderId).items.push(pageItem);
        return;
      }

      rootItems.push(pageItem);
    });

    return {
      pageItems: sortTreeItemsAlphabetically(rootItems),
      initialExpandedPageItemIds: Array.from(idToFolder.keys()),
    };
  }, [idToPage, currentLocale, folders]);

  const languageItems = useMemo(() => {
    const filteredUsedLocales = usedLocales.filter((code) => code !== currentLocale);

    const usedLanguageOptions = helpers.getRelatedLanguageOptions(
      supportedLocales.filter((code) => !filteredUsedLocales.includes(code)),
      languages,
      countries
    );

    return usedLanguageOptions.length
      ? [
          {
            id: 'used-languages',
            label: i18next.t('Your languages'),
            items: usedLanguageOptions.map((item) => ({
              id: item.code,
              value: item.code,
              label: item.name,
              flag: { code: item.countryCode },
            })),
          },
        ]
      : [];
  }, [currentLocale, usedLocales, supportedLocales, languages, countries]);

  const [pagesToOverride, isOnePageToOverride] = useMemo(() => {
    const overrides = [];
    const uniquePagePathsToOverride = new Set();

    const selectedPagePathsWithoutLocale = selectedPageIds
      .map((id) => {
        const page = idToPage.get(id);
        return page?.path.slice(page.locale.length + 1);
      })
      .filter(Boolean);

    pages.forEach((page) => {
      if (!selectedLanguages.includes(page.locale)) {
        return;
      }

      const pagePathWithoutLocale = page.path.slice(page.locale.length + 1);

      const isSamePath = selectedPagePathsWithoutLocale.some(
        (selectedPagePath) => selectedPagePath === pagePathWithoutLocale
      );

      if (isSamePath) {
        overrides.push(page);
        uniquePagePathsToOverride.add(pagePathWithoutLocale);
      }
    });
    return [overrides, uniquePagePathsToOverride.size === 1];
  }, [selectedPageIds, selectedLanguages, idToPage, pages]);

  const captions = useMemo(
    () => ({
      ...getCaptions({
        pagesToOverride,
        isOnePageToOverride,
        languageItems,
      }),
      ...propsCaptions,
    }),
    // eslint-disable-next-line yola/react-hooks/exhaustive-deps
    [selectedPageIds, pagesToOverride]
  );

  const overrideExistingPagesOptions = useMemo(
    () => [
      {
        label: captions.fields.overrideExistingPages.radioButton1,
        value: translateExistingPageOptionsValues.REPLACE_PAGE,
        isLabelBold: true,
      },
      {
        label: captions.fields.overrideExistingPages.radioButton2,
        value: translateExistingPageOptionsValues.COPY_PAGE,
        isLabelBold: true,
      },
    ],
    [captions.fields.overrideExistingPages]
  );

  const handleTranslatePage = async (submitData) => {
    const { pageIds, locales, autoTrans, existingPageResolving } = submitData;
    const overridePage = existingPageResolving === translateExistingPageOptionsValues.REPLACE_PAGE;
    const amountOfPagesToOverride = overridePage ? pagesToOverride.length : 0;

    const totalPagesToTranslate = pageIds.length * locales.length;
    const amountOfPagesToAdd = pages.length + totalPagesToTranslate - amountOfPagesToOverride;

    try {
      if (autoTrans) {
        await upsell.operations.requestFeatureAvailability(AI_LOCALIZATION, {
          isAiLocalizationEnabled,
        });
      }

      await upsell.operations.requestFeatureAvailability(PAGE_COUNT, {
        count: amountOfPagesToAdd,
      });
    } catch (error) {
      if (error instanceof Error) {
        throw error;
      }
      // eslint-disable-next-line no-console
      console.log('Upgrade flow was canceled');
      return;
    }

    const showProgressDialog = (jobData) => {
      const succeeded = jobData?.results?.succeeded || {};
      const current = Object.values(succeeded).flat().length || 1;

      dispatch(
        dialogs.actions.show(dialogTypes.TRANSLATION_PROGRESS_DIALOG, {
          overlay: 'visible',
          cancelable: false,
          progressType: 'indeterminate',
          width: 336,
          height: 109,
          minHeight: 109,
          captions: {
            title: `${i18next.t('Translating {current}/{total}...', {
              current,
              total: totalPagesToTranslate,
            })}`,
          },
        })
      );
    };

    showProgressDialog();
    trackEvent(events.TRANSLATE_PAGE_PROGRESS_DIALOG_DISPLAYED, { bulkEditing });

    let results;

    try {
      const data = await initiatePageLocalization({
        pageIds,
        locales,
        translateWithAi: autoTrans,
        ifPageExists: existingPageResolving,
        onPoll: showProgressDialog,
      });

      ({ results } = data);
    } catch (e) {
      if (autoTrans) {
        const errorType = e.results?.code || errorTypes.UNKNOWN_TRANSLATE_PAGE;

        dispatch(
          dialogs.actions.show(dialogTypes.AI_ERROR_DIALOG, {
            errorType,
            onRenew: () => {
              dispatch(dialogs.actions.hide());
            },
          })
        );
      } else {
        console.error(e);
        dispatch(dialogs.actions.show(dialogTypes.PAGE_MANAGER));
      }
      return;
    }

    await reloadSiteMeta();

    const failedPageIds = [
      ...new Set(Object.values(results.failed || {}).flatMap((pageMap) => Object.keys(pageMap))),
    ];

    if (failedPageIds.length > 0) {
      dispatch(
        dialogs.actions.show(dialogTypes.TRANSLATION_ERROR_DIALOG, {
          failedPageIds,
          onRetry: () => {
            dispatch(
              dialogs.actions.show(dialogTypes.TRANSLATE_PAGE, {
                initialValues: {
                  pageIds: failedPageIds,
                  locales,
                  autoTrans,
                  existingPageResolving,
                },
                bulkEditing,
              })
            );
          },
        })
      );

      return;
    }

    if (totalPagesToTranslate === 1) {
      const { succeeded } = results || {};
      const [[{ destinationPageId }]] = Object.values(succeeded || {});

      if (destinationPageId) {
        await site.operations.setActivePage(destinationPageId);
      }

      dialogs.operations.hide();
      saveSite();
    } else {
      const totalTranslatedPages = Object.values(results.succeeded).flat().length;
      const totalFailedPages = Object.values(results.failed).flat().length;

      dispatch(
        dialogs.actions.show(dialogTypes.TRANSLATION_COMPLETE_DIALOG, {
          width: 360,
          height: 186,
          minHeight: 186,
          captions: {
            title: i18next.t('Translation completed'),
            description: i18next.t(
              'Your pages are translated into the selected languages. Choose a language in the header to continue editing.'
            ),
            dismiss: i18next.t('Dismiss'),
          },
          totalTranslatedPages,
          totalFailedPages,
          onDismiss: () => {
            dialogs.operations.hide();
          },
        })
      );
    }
  };

  const showSelf = () => {
    dispatch(
      dialogs.actions.show(dialogTypes.TRANSLATE_PAGE, {
        initialValues: getValues(),
        bulkEditing,
        captions: propsCaptions,
      })
    );
  };

  const handleSubmit = (values) => {
    const { pageIds: targetPageIds, locales, autoTrans, existingPageResolving } = values;
    const overridePage = existingPageResolving === translateExistingPageOptionsValues.REPLACE_PAGE;

    onDialogMainAction({
      dialogType: dialogTypes.TRANSLATE_PAGE,
      locale: currentLocale,
      totalTargetPages: targetPageIds.length * locales.length,
      targetLocales: locales.join(', '),
      aiTranslation: autoTrans,
      overridePage,
      bulkEditing,
    });

    checkForUnsafe({
      onSubmit: handleTranslatePage,
      onCancel: showSelf,
    })(values);
  };

  const handleCancel = () => {
    onDialogCancel({ dialogType: dialogTypes.TRANSLATE_PAGE, bulkEditing });
    dialogs.operations.show(dialogTypes.PAGE_MANAGER);
  };

  const onWebsiteSettingsClick = (e) => {
    e.preventDefault();

    dialogs.operations.show(dialogTypes.WEBSITE_SETTINGS, {
      activeTabId: tabIdentifiers.MULTILINGUAL,
      onAfterDialogClose: showSelf,
      preventActivePageUpdateOnLocaleAdd: true,
    });
  };

  return (
    <TranslatePageDialog
      control={control}
      pageItems={pageItems}
      initialExpandedPageItemIds={initialExpandedPageItemIds}
      languageItems={languageItems}
      overrideExistingPagesOptions={overrideExistingPagesOptions}
      captions={captions}
      showOverrideExistingPages={pagesToOverride.length > 0}
      onWebsiteSettingsClick={onWebsiteSettingsClick}
      onSubmit={manageSubmit(handleSubmit)}
      onCancel={handleCancel}
    />
  );
}

TranslatePageDialogContainer.propTypes = {
  captions: PropTypes.shape({}),
  initialValues: PropTypes.shape({
    pageIds: PropTypes.arrayOf(PropTypes.string).isRequired,
    langCodes: PropTypes.arrayOf(PropTypes.string),
    autoTrans: PropTypes.bool,
  }).isRequired,
  bulkEditing: PropTypes.bool,
  onDialogMainAction: PropTypes.func.isRequired,
  onDialogCancel: PropTypes.func.isRequired,
};

export default TranslatePageDialogContainer;
